import { api } from '@/lib/api';
import { intlLocale } from '@/i18n';

export interface Website {
    id: number;
    code: string;
    name: string;
    theme: string;
    theme_version: string | null;
    theme_repository: string | null;
    /** Whether the theme repository has an access token; the token itself is never sent. */
    theme_repository_token_set?: boolean;
    default_locale: string;
    locales: string[];
    active: boolean;
    /** Hostname flagged canonical, or null when none is marked. */
    canonical_host: string | null;
    /**
     * Live quota-counting usage (media + renditions + backups) so the list can
     * show a column without a request per row. The walked buckets — themes,
     * attachments, cache — are not in this figure; the detail card has them.
     */
    storage_used_bytes?: number;
    /** Null = no quota. */
    storage_quota_bytes?: number | null;
    /**
     * True = the public site sends noindex on every response and serves a
     * Disallow-all robots.txt. Meant for sites still being built. In the
     * list payload too, so a forgotten one is visible without opening it.
     */
    search_noindex?: boolean;
    /**
     * True = the public site serves a holding page and a 503 to everyone
     * whose address is not on the allow list. Stronger than search_noindex,
     * which only asks crawlers to look away: this one stops people. In the
     * list payload so a site left hidden after launch is visible without
     * opening it, which is the failure this feature invites.
     */
    under_construction?: boolean;
    /**
     * Set while the website is deleted and waiting for its purge, which
     * removes it with its files for good at purge_at. Only super admins see
     * a deleted website.
     */
    deleted_at?: string | null;
    purge_at?: string | null;
}

export interface WebsiteHost {
    id: number;
    hostname: string;
    canonical: boolean;
    /**
     * The page on the canonical host this hostname stands for. Set on a
     * vanity domain that names one specific page, so a visitor who typed it
     * off a flyer lands there instead of on the homepage. Null redirects to
     * whatever path was asked for, which is what a defensive spelling of the
     * main brand wants.
     */
    redirect_path: string | null;
}

/** Per-locale text overrides for one form. */
export interface FormLocaleLabels {
    /** field-name → label text */
    labels: Record<string, string>;
    /** field-name → placeholder text */
    placeholders: Record<string, string>;
    /** 'submit' → button text */
    buttons: Record<string, string>;
}

/** Scraped template defaults (locale-neutral — read from the rendered HTML). */
export type FormLabels = FormLocaleLabels;

export interface FormConfig {
    recipient: string | null;
    subject: string | null;
    success_url: string | null;
    /**
     * Per-form sender override. Null/empty falls back to the website's
     * default `mail_from_email` / `mail_from_name`. `from_name` falls
     * through to the website default name → website name.
     */
    from_email: string | null;
    from_name: string | null;
    /** locale → per-locale text overrides for labels, placeholders, and button text. */
    labels?: Record<string, FormLocaleLabels> | null;
    /**
     * How long files uploaded through this form are kept, in days.
     * Null = the platform default of 90 days. After this the files are
     * deleted from disk; the submission itself is kept and shows the
     * attachment as expired.
     */
    attachment_retention_days?: number | null;
}

/** Retention applied when a form sets no override of its own. */
export const DEFAULT_ATTACHMENT_RETENTION_DAYS = 90;

export interface ModuleToggle {
    enabled: boolean;
}

export interface WebsiteDetail extends Website {
    hosts: WebsiteHost[];
    forms: Record<string, FormConfig>;
    /** Raw stored toggles per module id. */
    modules: Record<string, ModuleToggle>;
    /** Resolver-computed: enabled AND all deps satisfied. */
    modules_effective: Record<string, boolean>;
    /**
     * Default per-website "From:" address for all outgoing mail.
     * Per-channel overrides live on each module's settings entity
     * (CheckoutSettings, CustomerSettings) and per-form (in the
     * form's JSON entry under `forms`). Admin-only on the API.
     */
    mail_from_email?: string | null;
    /** Display name for the default From: header. */
    mail_from_name?: string | null;
    /** Cookie wall configuration (always present, returned resolved). */
    cookie_settings: CookieSettings;
    /**
     * Contact and company details for the site's own chrome: the phone
     * number in the header, the address in the footer, the KvK number on
     * the terms page. Always present, every key a string, blank when
     * unset — the API returns it resolved.
     */
    company: CompanyDetails;
    /** The floating WhatsApp button themes place (always present, returned resolved). */
    whatsapp_settings: WhatsappSettings;
    /**
     * Google Places resource id for this website's business listing
     * (admin-only; the public render never sees it). Null = reviews
     * sync is disabled for this website.
     */
    google_place_id?: string | null;
    /**
     * Addresses that reach the site while it is under construction, as CIDRs
     * (a bare address counts as one host). Admin-only on the API: it is the
     * guest list for a closed site.
     *
     * Added to the deployment-wide list, not a replacement for it — our own
     * offices and the ZenRise crawler live in that one and are not repeated
     * per site, so an empty list here does not mean nobody can get in.
     */
    construction_allow_ips?: string[];
    // storage_quota_bytes is inherited from Website — the list and the detail
    // carry the same field, so it is declared once.
}

export interface StorageBucketUsage {
    key: string;
    label: string;
    bytes: number;
    /** False for the image cache and in-flight uploads — shown, never charged. */
    counts: boolean;
}

export interface WebsiteStorage {
    /** Bytes the quota applies to. */
    used_bytes: number;
    /** Everything, including the buckets that don't count. */
    on_disk_bytes: number;
    quota_bytes: number | null;
    remaining_bytes: number | null;
    percent_used: number | null;
    buckets: StorageBucketUsage[];
    /**
     * Oldest walked measurement (ISO-8601), or null if nothing has been walked
     * yet. Media/renditions/backups are always live; this dates the rest.
     */
    measured_at: string | null;
}

export interface CookieSettings {
    enabled: boolean;
    /** Bumped to re-prompt every visitor after a policy change. */
    version: number;
    policy_url: string;
    /** Plain text; banner renders with preserve-newlines. */
    read_more_text: string;
}

/**
 * Site-wide contact details. Every key is always present and always a
 * string: blank means "not filled in", so a theme can test the value
 * without a defined() guard and the form needs no null handling.
 *
 * The key list is owned by cms-core (Website::COMPANY_KEYS); the API
 * drops anything else, so adding a field here alone does nothing.
 */
export interface CompanyDetails {
    phone: string;
    email: string;
    whatsapp: string;
    street: string;
    postal_code: string;
    city: string;
    country: string;
    /** Dutch chamber of commerce number. */
    kvk: string;
    /** VAT / BTW number. */
    vat: string;
    facebook: string;
    instagram: string;
    tiktok: string;
    linkedin: string;
    youtube: string;
    /**
     * The business for search engines (core Seo\LocalBusiness), published
     * only as structured data. A schema.org LocalBusiness type from
     * BUSINESS_TYPES, or '' to stay an Organization.
     */
    business_type: string;
    /** One rule per line: `Mo-Fr 08:00-17:00`. */
    opening_hours: string;
    /** Places, comma-separated: `Breda, Tilburg`. */
    area_served: string;
    price_range: string;
    /** A year (`2008`) or a date (`2008-04-01`). */
    founding_date: string;
    latitude: string;
    longitude: string;
    /** The https:// link of the Google Maps listing. */
    google_maps: string;
}

/**
 * The LocalBusiness types the API accepts, in the same order: core's
 * Seo\LocalBusiness::TYPES. A type missing here can't be picked; one
 * missing there is refused on save.
 */
export const BUSINESS_TYPES = [
    'LocalBusiness',
    'HomeAndConstructionBusiness', 'GeneralContractor', 'HousePainter', 'RoofingContractor',
    'Electrician', 'Plumber', 'HVACBusiness', 'Locksmith', 'MovingCompany',
    'HealthAndBeautyBusiness', 'BeautySalon', 'HairSalon', 'NailSalon', 'DaySpa',
    'TattooParlor', 'HealthClub',
    'MedicalBusiness', 'Physician', 'Dentist', 'Physiotherapy', 'Optician',
    'FoodEstablishment', 'Restaurant', 'CafeOrCoffeeShop', 'Bakery', 'BarOrPub',
    'Store', 'ClothingStore', 'Florist', 'FurnitureStore', 'GardenStore', 'HardwareStore',
    'HomeGoodsStore', 'JewelryStore', 'ElectronicsStore', 'BikeStore', 'PetStore',
    'SportingGoodsStore', 'ToyStore', 'BookStore', 'ShoeStore',
    'AutomotiveBusiness', 'AutoDealer', 'AutoRepair', 'AutoPartsStore',
    'ProfessionalService', 'LegalService', 'Notary', 'AccountingService', 'FinancialService',
    'RealEstateAgent', 'EmploymentAgency', 'TravelAgency', 'ChildCare', 'DryCleaningOrLaundry',
    'LodgingBusiness', 'SportsActivityLocation', 'EntertainmentBusiness',
] as const;

/** A blank set, for a website that has never had them filled in. */
export function emptyCompanyDetails(): CompanyDetails {
    return {
        phone: '', email: '', whatsapp: '',
        street: '', postal_code: '', city: '', country: '',
        kvk: '', vat: '',
        facebook: '', instagram: '', tiktok: '', linkedin: '', youtube: '',
        business_type: '', opening_hours: '', area_served: '', price_range: '', founding_date: '',
        latitude: '', longitude: '', google_maps: '',
    };
}

/**
 * The floating WhatsApp button a theme places with
 * `{{ cms_whatsapp_button() }}` (Website::resolveWhatsappSettings()).
 */
export interface WhatsappSettings {
    /** On by default: it only shows where a theme placed it, and only with a number. */
    enabled: boolean;
    /** Blank = the company WhatsApp number. */
    number: string;
    /** Text the chat opens with, per locale. */
    messages: Record<string, string>;
    /** Text next to the icon, per locale. */
    labels: Record<string, string>;
    /** ISO alpha-2 codes; empty = shown in every country. */
    countries: string[];
}

export function emptyWhatsappSettings(): WhatsappSettings {
    return { enabled: true, number: '', messages: {}, labels: {}, countries: [] };
}

export async function fetchWebsites(): Promise<{ items: Website[] }> {
    return api('/api/admin/websites');
}

export async function fetchWebsite(id: number): Promise<WebsiteDetail> {
    return api(`/api/admin/websites/${id}`);
}

export async function fetchWebsiteStorage(id: number): Promise<WebsiteStorage> {
    return api(`/api/admin/websites/${id}/storage`);
}

/** 1.4 GB, 812 MB, … — the only place the admin formats byte counts. */
export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes / 1024;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i++;
    }
    // Decimal comma in Dutch: formatted at call time, in the UI language.
    const number = new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
    return `${number} ${units[i]}`;
}

export async function createWebsite(payload: Partial<Website>): Promise<WebsiteDetail> {
    return api('/api/admin/websites', { method: 'POST', body: payload });
}

export async function updateWebsite(id: number, payload: Partial<Website>): Promise<WebsiteDetail> {
    return api(`/api/admin/websites/${id}`, { method: 'PATCH', body: payload });
}

/** Takes the site offline now; it is purged at purge_at unless undone. */
export async function deleteWebsite(id: number): Promise<{ deleted_at: string; purge_at: string }> {
    return api(`/api/admin/websites/${id}`, { method: 'DELETE' });
}

export async function undeleteWebsite(id: number): Promise<Website> {
    return api(`/api/admin/websites/${id}/undelete`, { method: 'POST' });
}

export async function selectWebsite(id: number): Promise<void> {
    await api(`/api/admin/websites/${id}/select`, { method: 'POST' });
}

export async function clearWebsiteSelect(): Promise<void> {
    await api(`/api/admin/websites/clear-select`, { method: 'POST' });
}

/**
 * The caller's own public address, as cms-public will see it.
 *
 * Only used to prefill the under-construction allow list. Typing that address
 * by hand is where this feature goes wrong: one wrong digit locks you out of
 * the site you are building, and the screen that would tell you so is the one
 * you can no longer reach.
 */
export async function fetchClientIp(): Promise<string | null> {
    const res = await api<{ ip: string | null }>('/api/admin/websites/client-ip');
    return res?.ip ?? null;
}

export async function addHost(websiteId: number, payload: { hostname: string; canonical?: boolean; redirect_path?: string | null }): Promise<WebsiteHost> {
    return api(`/api/admin/websites/${websiteId}/hosts`, { method: 'POST', body: payload });
}

export async function deleteHost(websiteId: number, hostId: number): Promise<void> {
    await api(`/api/admin/websites/${websiteId}/hosts/${hostId}`, { method: 'DELETE' });
}

export async function setCanonicalHost(websiteId: number, hostId: number): Promise<WebsiteDetail> {
    return api(`/api/admin/websites/${websiteId}/hosts/${hostId}/canonical`, { method: 'POST' });
}

/**
 * Update a single form's configuration on a website. Editor-accessible
 * (the page-edit cog uses this); the website-wide updateWebsite remains
 * super-only because it can rename, change theme, etc.
 */
export async function updateWebsiteForm(websiteId: number, formId: string, config: FormConfig): Promise<{ form: FormConfig }> {
    return api(`/api/admin/websites/${websiteId}/forms/${encodeURIComponent(formId)}`, {
        method: 'PATCH',
        body: config,
    });
}

export async function deleteWebsiteForm(websiteId: number, formId: string): Promise<void> {
    await api(`/api/admin/websites/${websiteId}/forms/${encodeURIComponent(formId)}`, { method: 'DELETE' });
}

/**
 * Trigger an immediate Google reviews sync (the daily ReviewsSchedule
 * also runs this; this endpoint lets an admin pull reviews now after
 * configuring the Place id + API key without waiting until 04:00 UTC).
 */
export async function syncGoogleReviews(websiteId: number): Promise<{ ok: boolean; queued: boolean }> {
    return api(`/api/admin/websites/${websiteId}/google-reviews/sync`, { method: 'POST' });
}

/**
 * One per-website backup bundle (DB rows + media tree). Worker-owned
 * lifecycle: queued → running → success | failed. `kind` records how it
 * was created — `pre-restore` rows are the safety snapshot taken just
 * before a restore.
 */
export interface WebsiteBackup {
    id: string;
    status: 'queued' | 'running' | 'success' | 'failed';
    kind: 'manual' | 'scheduled' | 'pre-restore' | 'imported' | 'final';
    filename: string | null;
    bytes_size: number | null;
    output: string | null;
    duration_ms: number | null;
    created_at: string;
    updated_at: string;
}

/** One restore of the website from a backup, and what became of it. */
export interface WebsiteRestore {
    id: string;
    status: 'queued' | 'running' | 'success' | 'failed';
    backup_id: string;
    pre_restore_backup_id: string | null;
    /** Why it failed; null otherwise. */
    output: string | null;
    requested_by: string | null;
    created_at: string;
    finished_at: string | null;
}

export async function fetchBackups(websiteId: number): Promise<{ items: WebsiteBackup[]; last_restore: WebsiteRestore | null }> {
    return api(`/api/admin/websites/${websiteId}/backups`);
}

/** Enqueue a manual backup. Returns the freshly-queued row. */
export async function createBackup(websiteId: number): Promise<{ enqueued: boolean; status: WebsiteBackup }> {
    return api(`/api/admin/websites/${websiteId}/backups`, { method: 'POST' });
}

/**
 * Enqueue an in-place restore from a backup. Destructive — the API first
 * takes a `pre-restore` safety backup, then dispatches the restore.
 */
export async function restoreBackup(websiteId: number, backupId: string): Promise<{ enqueued: boolean; restore: WebsiteRestore }> {
    return api(`/api/admin/websites/${websiteId}/backups/${backupId}/restore`, { method: 'POST' });
}

export async function deleteBackup(websiteId: number, backupId: string): Promise<void> {
    await api(`/api/admin/websites/${websiteId}/backups/${backupId}`, { method: 'DELETE' });
}
